/**
 * StableBatchedMesh — three.js `BatchedMesh` with a stable draw-slot map.
 *
 * Why: under multiview (IWSDK on Quest through the super-three fork) texture
 * uploads are deferred to the end of the frame. `BatchedMesh` keeps its
 * slot → instance map in a DataTexture and rebuilds it every frame with the
 * default `sortObjects` / `perObjectFrustumCulled`, so the CPU draw list and
 * the GPU map can be one frame apart — a wrong instance per slot for a frame.
 * See docs/02-QUEST_PERFORMANCE.md §6.1 and the repository-level multiview note.
 *
 * How: slot i is instance i, forever. The indirect texture is written once.
 * Hidden or frustum-culled instances become zero-count entries in the
 * multi-draw arrays, which the GPU skips. Per-frame frustum culling stays on
 * (`cullPerFrame`) and writes zeros instead of compacting the list.
 *
 * It reads the same private fields `BatchedMesh` itself uses, which pins the
 * three.js version — the `three` alias already does that.
 */
import { BatchedMesh, Frustum, Matrix4, Sphere, type BufferGeometry, type Camera, type CoordinateSystem, type Material } from "three";

interface InstanceInfo {
  visible: boolean;
  active: boolean;
  geometryIndex: number;
}

interface GeometryInfo {
  start: number;
  count: number;
}

interface Internals {
  _instanceInfo: InstanceInfo[];
  _geometryInfo: GeometryInfo[];
  _multiDrawStarts: Int32Array;
  _multiDrawCounts: Int32Array;
  _multiDrawCount: number;
  _indirectTexture: { image: { data: Uint32Array }; needsUpdate: boolean };
  _visibilityChanged: boolean;
}

interface ArrayCameraLike extends Camera {
  isArrayCamera?: boolean;
  cameras?: Camera[];
}

const _matrix = new Matrix4();
const _sphere = new Sphere();
const _frustum = new Frustum();
const _frustums: Frustum[] = [];

export class StableBatchedMesh extends BatchedMesh {
  /** Keep per-frame frustum culling (writes zero counts, never compacts). */
  cullPerFrame = true;
  /** The frustum-culled count of the last frame, for stats. */
  lastCulled = 0;
  /** Draw slots submitted last frame (always the instance count). */
  lastSlots = 0;

  private slotsReady = false;

  constructor(maxInstanceCount: number, maxVertexCount: number, maxIndexCount: number, material?: Material) {
    super(maxInstanceCount, maxVertexCount, maxIndexCount, material);
    // The two defaults that make the slot map churn every frame.
    this.perObjectFrustumCulled = false;
    this.sortObjects = false;
  }

  private get internals(): Internals {
    return this as unknown as Internals;
  }

  /** Any structural change (a new instance, a deleted one) invalidates the once-written map. */
  override addInstance(geometryId: number): number {
    this.slotsReady = false;
    return super.addInstance(geometryId);
  }

  override deleteInstance(instanceId: number): this {
    this.slotsReady = false;
    return super.deleteInstance(instanceId);
  }

  override onBeforeRender(
    _renderer: unknown,
    _scene: unknown,
    camera: Camera,
    geometry: BufferGeometry,
    _material: Material,
    _group: unknown,
  ): void {
    const it = this.internals;
    const instances = it._instanceInfo;

    if (!this.slotsReady) {
      const ids = it._indirectTexture.image.data;
      for (let i = 0; i < instances.length; i++) ids[i] = i;
      it._indirectTexture.needsUpdate = true; // the only upload it ever needs
      this.slotsReady = true;
      it._visibilityChanged = true;
    }

    if (!it._visibilityChanged && !this.cullPerFrame) return;

    const index = geometry.getIndex();
    const bytesPerElement = index === null ? 1 : index.array.BYTES_PER_ELEMENT;
    const frustums = this.cullPerFrame ? this.localFrustums(camera) : [];
    const starts = it._multiDrawStarts;
    const counts = it._multiDrawCounts;
    const geometries = it._geometryInfo;
    let culled = 0;

    for (let i = 0; i < instances.length; i++) {
      const info = instances[i]!;
      const g = geometries[info.geometryIndex];
      let drawn = info.active && info.visible && g !== undefined && g.count > 0;
      if (drawn && frustums.length > 0) {
        this.getBoundingSphereAt(info.geometryIndex, _sphere);
        this.getMatrixAt(i, _matrix);
        _sphere.applyMatrix4(_matrix);
        let inside = false;
        for (const f of frustums) {
          if (f.intersectsSphere(_sphere)) {
            inside = true;
            break;
          }
        }
        if (!inside) {
          drawn = false;
          culled += 1;
        }
      }
      starts[i] = drawn ? g!.start * bytesPerElement : 0;
      counts[i] = drawn ? g!.count : 0;
    }

    it._multiDrawCount = instances.length;
    it._visibilityChanged = false;
    this.lastCulled = culled;
    this.lastSlots = instances.length;
  }

  /** One frustum per camera (two under XR), expressed in this mesh's local space. */
  private localFrustums(camera: Camera): Frustum[] {
    const array = camera as ArrayCameraLike;
    const cameras = array.isArrayCamera && array.cameras && array.cameras.length > 0 ? array.cameras : [camera];
    while (_frustums.length < cameras.length) _frustums.push(new Frustum());
    const out: Frustum[] = [];
    for (let i = 0; i < cameras.length; i++) {
      const c = cameras[i]!;
      _matrix.multiplyMatrices(c.projectionMatrix, c.matrixWorldInverse).multiply(this.matrixWorld);
      const f = cameras.length === 1 ? _frustum : _frustums[i]!;
      f.setFromProjectionMatrix(_matrix, (c as { coordinateSystem?: CoordinateSystem }).coordinateSystem, (c as { reversedDepth?: boolean }).reversedDepth);
      out.push(f);
    }
    return out;
  }
}
